'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface CarouselItem {
  id: number;
  type: 'article' | 'listing' | 'job';
  title: string;
  description: string;
  image: string;
  alt: string;
  link: string;
  badge?: string;
}

interface HeroCarouselProps {
  items: CarouselItem[];
}

const HeroCarousel = ({ items }: HeroCarouselProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    if (!isHydrated) return;

    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % items.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [items.length, isHydrated]);

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
  };

  const goToPrevious = () => {
    setCurrentIndex((prevIndex) => (prevIndex - 1 + items.length) % items.length);
  };

  const goToNext = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % items.length);
  };

  if (!isHydrated) {
    return (
      <div className="relative h-96 lg:h-[500px] bg-muted overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-black/30" />
        <div className="absolute bottom-8 left-8 right-8">
          <div className="h-8 bg-white/20 rounded mb-4 animate-pulse" />
          <div className="h-4 bg-white/20 rounded w-3/4 animate-pulse" />
        </div>
      </div>
    );
  }

  return (
    <div className="relative h-96 lg:h-[500px] bg-secondary overflow-hidden group">
      {/* Main Carousel Content */}
      <div className="relative h-full">
        {items.map((item, index) => (
          <div
            key={item.id}
            className={`absolute inset-0 transition-opacity duration-500 ${
              index === currentIndex ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <AppImage
              src={item.image}
              alt={item.alt}
              className="w-full h-full object-cover"
              priority={index === 0}
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-black/30" />
            
            {/* Container for centered content - 80% width */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-4/5 max-w-7xl px-8 lg:px-12">
                {item.badge && (
                  <span className="inline-block bg-primary text-primary-foreground px-3 py-1 rounded text-sm font-heading font-bold uppercase tracking-wide mb-4">
                    {item.badge}
                  </span>
                )}
                <h2 className="text-2xl lg:text-4xl font-heading font-bold text-white mb-4 leading-tight">
                  {item.title}
                </h2>
                <p className="text-lg text-white/90 mb-6 max-w-2xl">
                  {item.description}
                </p>
                <Link
                  href={item.link}
                  className="inline-flex items-center space-x-2 bg-primary text-primary-foreground px-6 py-3 rounded-lg font-heading font-bold uppercase tracking-wide text-sm hover:bg-accent transition-industrial"
                >
                  <span>Read More</span>
                  <Icon name="ArrowRightIcon" size={16} />
                </Link>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Navigation Arrows */}
      <button
        onClick={goToPrevious}
        className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center transition-industrial opacity-0 group-hover:opacity-100"
      >
        <Icon name="ChevronLeftIcon" size={24} />
      </button>
      
      <button
        onClick={goToNext}
        className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center transition-industrial opacity-0 group-hover:opacity-100"
      >
        <Icon name="ChevronRightIcon" size={24} />
      </button>

      {/* Dots Indicator */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex space-x-2">
        {items.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`w-3 h-3 rounded-full transition-industrial ${
              index === currentIndex ? 'bg-primary' : 'bg-white/50 hover:bg-white/70'
            }`}
          />
        ))}
      </div>
    </div>
  );
};

export default HeroCarousel;